import React from 'react';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function KropsterapiPage() {
  return (
    <>
      <SeoHead
        title="Kropsterapi – Niels Boye | Ibjerget på Midtfyn"
        description="Kropsterapi v. Niels Boye på Ibjerget. Intuitiv kropsterapi, holdningskorrektion, energiarbejde og forløsning af spændinger og ubalancer."
        canonicalUrl="https://www.ibjerget.dk/kropsterapi"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/behandlerrum.webp', { width: 1400, quality: 80, fit: 'cover' })}
            alt="Det lyse behandlerrum på Ibjerget"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/75 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-hand-holding-heart" aria-hidden="true"></i>
            <span>Terapeut Niels Boye</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            Kropsterapi
          </h1>
          <p className="mt-6 text-base sm:text-xl text-[#f7eefa] max-w-2xl mx-auto leading-relaxed font-normal">
            Intuitivt krops- og energiarbejde, der genskaber balancen og aktiverer kroppens egen selvhelbredende kraft.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#booking"
              className="px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md transition-colors"
            >
              Book tid hos Niels
            </a>
            <a
              href="tel:66157902"
              className="px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone mr-2 text-[#f7eefa]"></i>
              Ring 66 15 79 02
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Text Content */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
                Behandling & Tilgang
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                Vide rammer til det hele menneske
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Kropsterapi er et vidt begreb, og som jeg bruger det, er der behov for vide rammer:
                </p>
                <p>
                  Jeg tager udgangspunkt i, hvad der er af udfordringer og lægger en plan for, hvad vi skal begynde med. Jeg arbejder meget intuitivt, så det kan godt være, at du kommer med ondt i ryggen og det ene ben længere end det andet. Så vil det være naturligt at rette op på psoasmusklen, så hofterne står lige. Det kan forplante sig til, at vi skal arbejde med følgerne af skæv holdning gennem måske mange år. Måske skal vi arbejde med en hæmmet vejrtrækning, og måske kommer der nogle svære følelser frem. Det kan også være en frossen skulder eller tennisalbue, der er problemet. Her vil jeg typisk først forløse spændingen og give nogle øvelser, du kan arbejde videre med selv.
                </p>
                <p>
                  Når jeg arbejder, bruger jeg en kombination af mange forskellige kropsterapeutiske teknikker, jeg gennem årene har lært og samtidig visualiserer jeg (energiarbejde) knoglerne på plads, så jeg dermed (gen-) aktiverer kroppens evne til selv at skabe balance.
                </p>
              </div>

              {/* Faser */}
              <div className="pt-6 space-y-5">
                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Indledende samtale
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Vi tager en snak om, hvad du oplever som problematisk, og jeg ser efter holdningsforstyrrelser og spændingstilstande i forhold til, hvad du fortæller. I en kropsbehandling kan man ikke altid gå til problemets kerne, da nogle spændinger er betinget af andre. Derfor kan der blive tale om flere behandlinger.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Selve behandlingen
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Typisk er der behov for kort tid med healing og polaritetsbehandling, indtil kroppen slapper af, da det er en forudsætning for at slippe en overspænding permanent. Herefter vil jeg arbejde med specifikke områder for at give kroppen fri, så den kan helbrede sig selv. Ofte lader jeg mig guide af intuitionen i forhold til, hvad jeg først tager fat på.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Integration
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Da jeg arbejder både kropsligt og i energisystemet, kan du forvente, at du vil være lettere labil i nogle dage.
                  </p>
                  <p className="text-sm text-[#4e3568] leading-relaxed mt-2">
                    Efter behandlingen vil jeg kunne give et bud på, om der skal flere behandlinger til samt hvilke "lavthængende frugter" – dvs. nemme forløsninger jeg ser.
                  </p>
                </div>
              </div>

            </div>

            {/* Right: Mood photo & Practical Info */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Photo: behandlerrum.webp */}
              <div className="rounded-3xl overflow-hidden shadow-lg border border-[#edd5e8] bg-white">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/behandlerrum.webp', { width: 800, quality: 85, fit: 'cover' })}
                  alt="Det lyse behandlerrum med briks på Ibjerget"
                  className="w-full h-auto object-cover"
                />
                <div className="p-4 bg-[#fcf8fb] text-xs text-[#9b1b66] text-center border-t border-[#edd5e8]">
                  Det lyse behandlerrum på Ibjerget med trægulv, briks og udsigt til naturen
                </div>
              </div>

              {/* Praktisk information */}
              <div id="booking" className="bg-[#381d6e] text-[#faf5f9] rounded-3xl p-8 border border-[#5f2a99] shadow-lg space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block mb-1">
                    Fakta & Booking
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-white">
                    Praktisk information
                  </h3>
                </div>

                <div className="space-y-3.5 text-sm">
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Varighed:</span>
                    <span className="font-semibold text-white">Ca. 60 minutter</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Pris:</span>
                    <span className="font-semibold text-white">500 kr.</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Sted:</span>
                    <span className="font-semibold text-white text-right">Ibjergvej 12, 5792 Årslev</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Afbud:</span>
                    <span className="font-semibold text-white">Senest 24 timer før</span>
                  </div>
                </div>

                {/* Booking til Niels Boye */}
                <div className="pt-4 border-t border-[#5f2a99] space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wider text-[#e895c8] font-semibold">
                      Behandler
                    </p>
                    <p className="text-lg font-serif font-bold text-white">
                      Niels Boye
                    </p>
                    <p className="text-xs text-[#f7eefa]">
                      Kontakt Niels direkte for tidsbestilling og spørgsmål:
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 pt-2">
                    <a
                      href="tel:66157902"
                      className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Ring på 66 15 79 02</span>
                    </a>
                    <a
                      href="mailto:niels@ibjerget.dk"
                      className="flex items-center justify-center gap-3 w-full py-3 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
                    >
                      <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                      <span>Skriv til niels@ibjerget.dk</span>
                    </a>
                  </div>

                  <div className="p-3 bg-[#4a2282] rounded-xl text-xs text-[#f7eefa] leading-relaxed">
                    <p>
                      <strong>Bemærk:</strong> Niels er Elektro Hyper Sensitiv, og derfor skal mobiltelefon og eventuel smartwatch være slukket under behandlingen.
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
